import { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  format,
  parse,
  startOfWeek,
  subDays,
  startOfMonth,
  endOfMonth,
  startOfQuarter,
  endOfQuarter,
  startOfYear,
  endOfYear,
  subMonths,
  addMonths,
  subQuarters,
} from 'date-fns';
import {
  Play,
  Clock,
  CheckCircle,
  XCircle,
  Download,
  Trash2,
  Eye,
  FileSpreadsheet,
  RefreshCw,
  Loader2,
  ChevronDown,
  ChevronUp,
  Calendar,
  BarChart3,
  Sun,
  Moon,
  Terminal,
  BarChart,
  X,
  LogOut,
} from 'lucide-react';
import api from '../services/api';
import OpsDashboardContent from '../components/ops/OpsDashboardContent';
import RevenueDashboardContent from '../components/revenue/RevenueDashboardContent';
import MarketingDashboardContent from '../components/marketing/MarketingDashboardContent';
import ContractsDashboardContent from '../components/contracts/ContractsDashboardContent';
import EsmsDashboardContent from '../components/esms/EsmsDashboardContent';
import PancakeDashboardContent from '../components/pancake/PancakeDashboardContent';
import { useAuth } from '../contexts/AuthContext';
import ConfirmModal from '../components/common/ConfirmModal';
import AlertModal from '../components/common/AlertModal';
import DatePicker from '../components/DatePicker';

interface RunStatus {
  id: string;
  reportType?: 'weekly' | 'period' | 'monthly';
  startedAt: string;
  completedAt?: string;
  status: 'running' | 'completed' | 'failed';
  progress: number;
  currentStep?: string;
  error?: string;
  dateRange: {
    startDate: string;
    endDate: string;
  };
}

interface ReportVersion {
  id: string;
  reportType?: 'weekly' | 'period' | 'monthly';
  timestamp: string;
  dateRange: {
    startDate: string;
    endDate: string;
  };
  status: 'completed' | 'failed';
  files: string[];
  weeksProcessed: number;
  monthsProcessed: number;
}

interface PreviewData {
  headers: string[];
  rows: string[][];
  totalRows: number;
  previewRows: number;
}

// Report types for tabs
const REPORT_TABS = [
  {
    id: 'paying-atrisk',
    label: 'Paying & At-risk',
    description: 'Báo cáo paying members và at-risk hàng tuần',
    icon: BarChart3,
    active: true
  },
  {
    id: 'ops-dashboard',
    label: 'OPS Dashboard',
    description: 'Booking check-in analytics, capacity, heatmaps & quality ratings',
    icon: BarChart3,
    active: true
  },
  {
    id: 'revenue',
    label: 'Revenue',
    description: 'Báo cáo doanh thu',
    icon: BarChart3,
    active: true
  },
  {
    id: 'marketing',
    label: 'Marketing',
    description: 'Facebook Ads analytics & hiệu suất quảng cáo',
    icon: BarChart3,
    active: true
  },
  {
    id: 'contracts',
    label: 'Hợp đồng',
    description: 'Quản lý hợp đồng và chứng từ khách hàng',
    icon: BarChart3,
    active: true
  },
  {
    id: 'pancake',
    label: 'Pancake',
    description: 'Conversations, statistics, customers & posts từ Pancake.vn',
    icon: BarChart3,
    active: true
  },
  {
    id: 'esms',
    label: 'eSMS',
    description: 'Thống kê SMS, chi phí và số dư tài khoản eSMS.vn',
    icon: BarChart3,
    active: true
  },
];

const PERIOD_PRESETS = [
  {
    id: 'this-month',
    label: 'Tháng này',
    getRange: () => {
      const now = new Date();
      return { start: startOfMonth(now), end: endOfMonth(now) };
    }
  },
  {
    id: 'next-month',
    label: 'Tháng sau',
    getRange: () => {
      const nextMonth = addMonths(new Date(), 1);
      return { start: startOfMonth(nextMonth), end: endOfMonth(nextMonth) };
    }
  },
  {
    id: 'last-month',
    label: 'Tháng trước',
    getRange: () => {
      const lastMonth = subMonths(new Date(), 1);
      return { start: startOfMonth(lastMonth), end: endOfMonth(lastMonth) };
    }
  },
  {
    id: 'this-quarter',
    label: 'Quý này',
    getRange: () => {
      const now = new Date();
      return { start: startOfQuarter(now), end: endOfQuarter(now) };
    }
  },
  {
    id: 'last-quarter',
    label: 'Quý trước',
    getRange: () => {
      const lastQuarter = subQuarters(new Date(), 1);
      return { start: startOfQuarter(lastQuarter), end: endOfQuarter(lastQuarter) };
    }
  },
  {
    id: 'this-year',
    label: 'Năm nay',
    getRange: () => {
      const now = new Date();
      return { start: startOfYear(now), end: endOfYear(now) };
    }
  },
];

const MONTHLY_PRESETS = [
  {
    id: 'last-3-months',
    label: '3 tháng trước',
    getRange: () => {
      const now = new Date();
      const start = startOfMonth(subMonths(now, 2));
      const end = endOfMonth(now);
      return { start, end };
    }
  },
  {
    id: 'last-6-months',
    label: '6 tháng trước',
    getRange: () => {
      const now = new Date();
      const start = startOfMonth(subMonths(now, 5));
      const end = endOfMonth(now);
      return { start, end };
    }
  },
  {
    id: 'next-3-months',
    label: '3 tháng tới',
    getRange: () => {
      const now = new Date();
      const start = startOfMonth(now);
      const end = endOfMonth(addMonths(now, 2));
      return { start, end };
    }
  },
  {
    id: 'next-6-months',
    label: '6 tháng tới',
    getRange: () => {
      const now = new Date();
      const start = startOfMonth(now);
      const end = endOfMonth(addMonths(now, 5));
      return { start, end };
    }
  },
  {
    id: 'this-year',
    label: 'Năm nay',
    getRange: () => {
      const now = new Date();
      return { start: startOfYear(now), end: endOfYear(now) };
    }
  },
  {
    id: 'last-year',
    label: 'Năm trước',
    getRange: () => {
      const now = new Date();
      const lastYear = new Date(now.getFullYear() - 1, 0, 1);
      return { start: startOfYear(lastYear), end: endOfYear(lastYear) };
    }
  },
];

export default function ReportRunner() {
  const { logout } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  // Theme state
  const [isDark, setIsDark] = useState(() => {
    const saved = localStorage.getItem('theme');
    return saved ? saved === 'dark' : true; // default dark
  });

  // Active tab - sync with URL ?tab= param
  const [activeTab, setActiveTabState] = useState(() => {
    const tabParam = searchParams.get('tab');
    const validTabs = REPORT_TABS.filter(t => t.active).map(t => t.id);
    return tabParam && validTabs.includes(tabParam) ? tabParam : 'paying-atrisk';
  });

  const setActiveTab = (tabId: string) => {
    setActiveTabState(tabId);
    const newParams = new URLSearchParams(searchParams);
    if (tabId === 'paying-atrisk') {
      newParams.delete('tab');
    } else {
      newParams.set('tab', tabId);
    }
    setSearchParams(newParams, { replace: true });
  };

  // Date state - default to last completed week (ending on last Sunday)
  const lastSunday = subDays(startOfWeek(new Date(), { weekStartsOn: 1 }), 1); // Sunday of last completed week
  const defaultEnd = lastSunday;
  const defaultStart = startOfWeek(lastSunday, { weekStartsOn: 1 });

  const [startDate, setStartDate] = useState(format(defaultStart, 'yyyy-MM-dd'));
  const [endDate, setEndDate] = useState(format(defaultEnd, 'yyyy-MM-dd'));
  const [selectedWeeks, setSelectedWeeks] = useState<number>(1);
  const [customWeeks, setCustomWeeks] = useState<string>('');

  // Initialize reportType from URL params
  const [reportType, setReportType] = useState<'weekly' | 'period' | 'monthly'>(() => {
    const typeParam = searchParams.get('type');
    return typeParam === 'monthly' ? 'monthly' : typeParam === 'period' ? 'period' : 'weekly';
  });

  const [selectedPeriodPreset, setSelectedPeriodPreset] = useState<string>('');
  const [selectedMonthlyPreset, setSelectedMonthlyPreset] = useState<string>('');

  // Function to update reportType and URL
  const updateReportType = (type: 'weekly' | 'period' | 'monthly') => {
    setReportType(type);
    setSearchParams({ type }, { replace: true });
  };

  // Handle monthly preset selection
  const handleMonthlyPreset = (presetId: string) => {
    const preset = MONTHLY_PRESETS.find(p => p.id === presetId);
    if (preset) {
      const range = preset.getRange();
      setStartDate(format(range.start, 'yyyy-MM-dd'));
      setEndDate(format(range.end, 'yyyy-MM-dd'));
      setSelectedMonthlyPreset(presetId);
    }
  };

  // Set date range based on number of weeks (using last Sunday as base)
  const setWeeksRange = (weeks: number) => {
    const end = subDays(startOfWeek(new Date(), { weekStartsOn: 1 }), 1); // last Sunday
    const start = startOfWeek(new Date(end.getTime() - (weeks - 1) * 7 * 24 * 60 * 60 * 1000), { weekStartsOn: 1 });
    setStartDate(format(start, 'yyyy-MM-dd'));
    setEndDate(format(end, 'yyyy-MM-dd'));
    setSelectedWeeks(weeks);
    setCustomWeeks('');
  };

  // Handle custom weeks input
  const handleCustomWeeks = (value: string) => {
    setCustomWeeks(value);
    const weeks = parseInt(value);
    if (weeks > 0 && weeks <= 52) {
      const end = subDays(startOfWeek(new Date(), { weekStartsOn: 1 }), 1); // last Sunday
      const start = startOfWeek(new Date(end.getTime() - (weeks - 1) * 7 * 24 * 60 * 60 * 1000), { weekStartsOn: 1 });
      setStartDate(format(start, 'yyyy-MM-dd'));
      setEndDate(format(end, 'yyyy-MM-dd'));
      setSelectedWeeks(weeks);
    }
  };

  // Handle period preset selection
  const handlePeriodPreset = (presetId: string) => {
    const preset = PERIOD_PRESETS.find(p => p.id === presetId);
    if (preset) {
      const range = preset.getRange();
      setStartDate(format(range.start, 'yyyy-MM-dd'));
      setEndDate(format(range.end, 'yyyy-MM-dd'));
      setSelectedPeriodPreset(presetId);
    }
  };

  // Run state - restore from localStorage if a report was running before reload
  const [isRunning, setIsRunning] = useState(() => !!localStorage.getItem('currentRunId'));
  const [currentRunId, setCurrentRunId] = useState<string | null>(() => localStorage.getItem('currentRunId'));
  const [runStatus, setRunStatus] = useState<RunStatus | null>(null);

  // Versions state
  const [versions, setVersions] = useState<ReportVersion[]>([]);
  const [isLoadingVersions, setIsLoadingVersions] = useState(true);
  const [expandedVersion, setExpandedVersion] = useState<string | null>(null);

  // Preview state
  const [previewData, setPreviewData] = useState<PreviewData | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [previewFile, setPreviewFile] = useState<{ versionId: string; fileName: string } | null>(null);


  // Confirm/Alert state
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    message: string;
    onConfirm: () => void;
  } | null>(null);
  const [alertModal, setAlertModal] = useState<{
    isOpen: boolean;
    type?: 'info' | 'success' | 'warning' | 'error';
    message: string;
  } | null>(null);

  // Log state
  const [logEntries, setLogEntries] = useState<Array<{
    id: string;
    timestamp: Date;
    message: string;
    type: 'info' | 'success' | 'error' | 'running';
  }>>([]);
  const logEndRef = useRef<HTMLDivElement>(null);

  // Save theme preference
  useEffect(() => {
    localStorage.setItem('theme', isDark ? 'dark' : 'light');
  }, [isDark]);

  // Sync reportType with URL params (for browser back/forward navigation)
  useEffect(() => {
    const typeParam = searchParams.get('type');
    const newType = typeParam === 'monthly' ? 'monthly' : typeParam === 'period' ? 'period' : 'weekly';

    // If no URL param, set default
    if (!typeParam) {
      setSearchParams({ type: 'weekly' }, { replace: true });
    } else if (newType !== reportType) {
      setReportType(newType);
    }
  }, [searchParams]);

  // Load versions on mount
  useEffect(() => {
    loadVersions();
  }, []);

  // Poll for run status and update logs
  useEffect(() => {
    if (!currentRunId || !isRunning) return;

    const interval = setInterval(async () => {
      try {
        const response = await api.get(`/reports/status/${currentRunId}`);
        const newStatus = response.data;
        setRunStatus(newStatus);

        // Update log entries based on status
        if (newStatus.currentStep) {
          setLogEntries(prev => {
            const lastEntry = prev[prev.length - 1];
            if (lastEntry?.type === 'running' && lastEntry.message === newStatus.currentStep) {
              return prev; // No change
            }

            // Update or add log entry
            const newEntry = {
              id: `${Date.now()}-${Math.random()}`,
              timestamp: new Date(),
              message: newStatus.currentStep,
              type: newStatus.status === 'running' ? 'running' as const :
                newStatus.status === 'completed' ? 'success' as const : 'error' as const,
            };

            // If last entry was running with same message, replace it
            if (lastEntry?.type === 'running') {
              return [...prev.slice(0, -1), newEntry];
            }
            return [...prev, newEntry];
          });
        }

        if (newStatus.status === 'completed' || newStatus.status === 'failed') {
          setIsRunning(false);
          localStorage.removeItem('currentRunId');
          if (newStatus.status === 'completed') {
            setLogEntries(prev => [...prev, {
              id: `${Date.now()}-completed`,
              timestamp: new Date(),
              message: 'Hoàn thành! Tất cả files đã được tạo.',
              type: 'success',
            }]);
          } else if (newStatus.error) {
            setLogEntries(prev => [...prev, {
              id: `${Date.now()}-error`,
              timestamp: new Date(),
              message: `Lỗi: ${newStatus.error}`,
              type: 'error',
            }]);
          }
          loadVersions();
        }
      } catch (error: any) {
        console.error('Error polling status:', error);
        if (error.response?.status === 404) {
          setIsRunning(false);
          setCurrentRunId(null);
          localStorage.removeItem('currentRunId');
        }
      }
    }, 2000);

    return () => clearInterval(interval);
  }, [currentRunId, isRunning]);

  // Auto-scroll log to bottom
  useEffect(() => {
    logEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [logEntries]);

  const loadVersions = async () => {
    setIsLoadingVersions(true);
    try {
      const response = await api.get('/reports/versions');
      setVersions(response.data);
    } catch (error) {
      console.error('Error loading versions:', error);
    } finally {
      setIsLoadingVersions(false);
    }
  };

  const startRun = async () => {
    // Validation for period reports
    if (reportType === 'period') {
      const start = new Date(startDate);
      const end = new Date(endDate);
      const daysDiff = Math.floor((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));

      if (daysDiff > 365) {
        setAlertModal({
          isOpen: true,
          type: 'warning',
          message: 'Period report date range cannot exceed 365 days. Please select a shorter period.',
        });
        return;
      }

      if (daysDiff < 1) {
        setAlertModal({
          isOpen: true,
          type: 'error',
          message: 'End date must be after start date.',
        });
        return;
      }
    }

    // Validation for monthly reports
    if (reportType === 'monthly') {
      const start = new Date(startDate);
      const end = new Date(endDate);
      const monthsDiff = (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth()) + 1;

      if (monthsDiff > 24) {
        setAlertModal({
          isOpen: true,
          type: 'warning',
          message: 'Monthly report date range cannot exceed 24 months. Please select a shorter period.',
        });
        return;
      }

      if (end <= start) {
        setAlertModal({
          isOpen: true,
          type: 'error',
          message: 'End date must be after start date.',
        });
        return;
      }
    }

    setIsRunning(true);
    setRunStatus(null);

    // Reset and initialize log
    setLogEntries([{
      id: `${Date.now()}-start`,
      timestamp: new Date(),
      message: `Bắt đầu chạy ${reportType === 'monthly' ? 'monthly' : reportType === 'period' ? 'period' : 'weekly'} report từ ${startDate} đến ${endDate}`,
      type: 'info',
    }]);

    try {
      const response = await api.post('/reports/run', {
        startDate,
        endDate,
        reportType,
      });

      setCurrentRunId(response.data.runId);
      localStorage.setItem('currentRunId', response.data.runId);
    } catch (error: any) {
      console.error('Error starting run:', error);
      setIsRunning(false);
      const errorMessage = error.response?.data?.error || error.message;
      setLogEntries(prev => [...prev, {
        id: `${Date.now()}-error`,
        timestamp: new Date(),
        message: `Lỗi: ${errorMessage}`,
        type: 'error',
      }]);
      setAlertModal({
        isOpen: true,
        type: 'error',
        message: `Lỗi: ${errorMessage}`,
      });
    }
  };

  const deleteVersion = (versionId: string) => {
    setConfirmModal({
      isOpen: true,
      message: 'Bạn có chắc muốn xóa version này?',
      onConfirm: async () => {
        setConfirmModal(null);
        try {
          await api.delete(`/reports/versions/${versionId}`);
          loadVersions();
        } catch (error: any) {
          console.error('Error deleting version:', error);
          setAlertModal({
            isOpen: true,
            type: 'error',
            message: error.response?.data?.error || 'Không thể xóa version',
          });
        }
      },
    });
  };

  const downloadFile = (versionId: string, fileName: string) => {
    const url = `${api.defaults.baseURL}/reports/versions/${versionId}/download/${fileName}`;
    window.open(url, '_blank');
  };

  const openPreview = useCallback(async (versionId: string, fileName: string) => {
    setPreviewLoading(true);
    setPreviewError(null);
    setPreviewFile({ versionId, fileName });

    try {
      const response = await api.get(`/reports/versions/${versionId}/preview/${fileName}?limit=50`);
      setPreviewData(response.data);
      setPreviewError(null);
    } catch (error: any) {
      console.error('Error loading preview:', error);
      setPreviewData(null);
      setPreviewError(error.response?.data?.error || error.message || 'Không thể tải dữ liệu');
    } finally {
      setPreviewLoading(false);
    }
  }, []);

  const formatTimestamp = (ts: string) => {
    try {
      return format(new Date(ts), 'dd/MM/yyyy HH:mm:ss');
    } catch {
      return ts;
    }
  };

  const formatDateRange = (start: string, end: string) => {
    try {
      return `${format(new Date(start), 'dd/MM/yyyy')} → ${format(new Date(end), 'dd/MM/yyyy')}`;
    } catch {
      return `${start} → ${end}`;
    }
  };

  // Theme classes
  const theme = {
    bg: isDark ? 'bg-[#0a0f1a]' : 'bg-gray-50',
    headerBorder: isDark ? 'border-white/5' : 'border-gray-200',
    card: isDark ? 'bg-white/[0.02] border-white/[0.06]' : 'bg-white border-gray-200 shadow-sm',
    cardHover: isDark ? 'hover:bg-white/[0.02]' : 'hover:bg-gray-50',
    cardInner: isDark ? 'bg-white/[0.02] border-white/[0.04]' : 'bg-gray-50 border-gray-100',
    text: isDark ? 'text-white' : 'text-gray-900',
    textMuted: isDark ? 'text-white/40' : 'text-gray-500',
    textSecondary: isDark ? 'text-white/70' : 'text-gray-700',
    input: isDark
      ? 'bg-white/[0.03] border-white/10 text-white focus:border-cyan-500/50'
      : 'bg-white border-gray-300 text-gray-900 focus:border-cyan-500',
    inputIcon: isDark ? 'text-white/30' : 'text-gray-400',
    divider: isDark ? 'divide-white/[0.04]' : 'divide-gray-100',
    borderFaint: isDark ? 'border-white/[0.04]' : 'border-gray-100',
    borderLight: isDark ? 'border-white/[0.06]' : 'border-gray-200',
    icon: isDark ? 'text-white/40' : 'text-gray-400',
    iconHover: isDark ? 'hover:text-white/80' : 'hover:text-gray-600',
    btnSecondary: isDark ? 'bg-white/5 text-white/70 hover:bg-white/10' : 'bg-gray-100 text-gray-700 hover:bg-gray-200',
    successBg: isDark ? 'bg-emerald-500/10' : 'bg-emerald-50',
    successText: isDark ? 'text-emerald-400' : 'text-emerald-600',
    successDot: isDark ? 'bg-emerald-400' : 'bg-emerald-500',
    errorBg: isDark ? 'bg-red-500/10' : 'bg-red-50',
    errorText: isDark ? 'text-red-400' : 'text-red-600',
    errorDot: isDark ? 'bg-red-400' : 'bg-red-500',
    infoBg: isDark ? 'bg-cyan-500/10' : 'bg-cyan-50',
    infoText: isDark ? 'text-cyan-400' : 'text-cyan-600',
    progressBg: isDark ? 'bg-white/5' : 'bg-gray-200',
    modalBg: isDark ? 'bg-[#0d1320]' : 'bg-white',
    modalOverlay: isDark ? 'bg-black/80' : 'bg-black/50',
    tableHeaderBg: isDark ? 'bg-[#0d1320]' : 'bg-gray-50',
    tableRowHover: isDark ? 'hover:bg-white/[0.02]' : 'hover:bg-gray-50',
  };

  return (
    <div className={`min-h-screen overflow-x-hidden ${theme.bg} transition-colors duration-300`}>
      {/* Subtle grid background */}
      <div
        className={`fixed inset-0 ${isDark ? 'opacity-[0.03]' : 'opacity-[0.4]'}`}
        style={{
          backgroundImage: isDark
            ? `linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)`
            : `linear-gradient(#e5e7eb 1px, transparent 1px), linear-gradient(90deg, #e5e7eb 1px, transparent 1px)`,
          backgroundSize: '60px 60px'
        }}
      />

      {/* Header */}
      <header className={`relative border-b ${theme.headerBorder} transition-colors duration-300`}>
        <div className="max-w-[1600px] mx-auto px-3 sm:px-4">
          {/* Top bar */}
          <div className="flex items-center justify-between py-5">
            <div className="flex items-center gap-4">
              <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-cyan-400 to-blue-500 flex items-center justify-center shadow-lg shadow-cyan-500/20">
                <BarChart3 className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className={`text-xl font-semibold tracking-tight ${theme.text}`}>
                  Report Runner
                </h1>
                <p className={`text-sm ${theme.textMuted}`}>
                  Chạy và quản lý báo cáo analytics
                </p>
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-2">
              {/* Theme Toggle */}
              <button
                onClick={() => setIsDark(!isDark)}
                className={`p-2.5 rounded-xl ${theme.card} border transition-all hover:scale-105`}
                title={isDark ? 'Chuyển sang Light Mode' : 'Chuyển sang Dark Mode'}
              >
                {isDark ? (
                  <Sun className="w-5 h-5 text-amber-400" />
                ) : (
                  <Moon className="w-5 h-5 text-indigo-500" />
                )}
              </button>

              {/* Logout */}
              <button
                onClick={logout}
                className={`p-2.5 rounded-xl ${theme.card} border transition-all hover:scale-105 hover:border-red-500/50`}
                title="Đăng xuất"
              >
                <LogOut className={`w-5 h-5 ${isDark ? 'text-white/60 hover:text-red-400' : 'text-gray-500 hover:text-red-500'}`} />
              </button>
            </div>
          </div>

          {/* Tab Navigation */}
          <nav className="flex gap-1 -mb-px overflow-x-auto scrollbar-none">
            {REPORT_TABS.map((tab) => {
              const isActive = activeTab === tab.id;
              const TabIcon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`relative shrink-0 px-3 sm:px-5 py-3 text-sm font-medium rounded-t-lg transition-all flex items-center gap-2 ${isActive
                    ? `${isDark ? 'bg-white/[0.05] text-cyan-400' : 'bg-white text-cyan-600'} border-t border-x ${theme.borderLight}`
                    : `${theme.textMuted} hover:${theme.text} ${isDark ? 'hover:bg-white/[0.02]' : 'hover:bg-gray-100'}`
                    }`}
                >
                  <TabIcon className="w-4 h-4" />
                  {tab.label}
                  {isActive && (
                    <span className={`absolute bottom-0 left-0 right-0 h-0.5 ${isDark ? 'bg-[#0a0f1a]' : 'bg-white'}`} />
                  )}
                </button>
              );
            })}
          </nav>
        </div>
      </header>

      <div className="relative max-w-[1600px] mx-auto px-3 sm:px-4 py-8">
        {/* Active Tab Title */}
        {(() => {
          const currentTab = REPORT_TABS.find(t => t.id === activeTab);
          return currentTab ? (
            <div className="mb-6">
              <h2 className={`text-lg font-semibold ${theme.text}`}>{currentTab.label}</h2>
              <p className={`text-sm ${theme.textMuted}`}>{currentTab.description}</p>
            </div>
          ) : null;
        })()}

        {activeTab === 'ops-dashboard' && <OpsDashboardContent isDark={isDark} theme={theme} />}

        {activeTab === 'revenue' && <RevenueDashboardContent isDark={isDark} theme={theme} />}

        {activeTab === 'marketing' && <MarketingDashboardContent isDark={isDark} theme={theme} />}

        {activeTab === 'contracts' && <ContractsDashboardContent isDark={isDark} theme={theme} />}

        {activeTab === 'pancake' && <PancakeDashboardContent isDark={isDark} theme={theme} />}

        {activeTab === 'esms' && <EsmsDashboardContent isDark={isDark} theme={theme} />}

        {activeTab === 'paying-atrisk' && <>
        {/* Report Type Tabs */}
        <div className="flex gap-2 mb-6">
          <button
            onClick={() => updateReportType('weekly')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              reportType === 'weekly'
                ? 'bg-cyan-500 text-white'
                : `${theme.btnSecondary} hover:bg-cyan-500/20`
            }`}
          >
            Weekly Reports
          </button>
          <button
            onClick={() => updateReportType('monthly')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              reportType === 'monthly'
                ? 'bg-orange-500 text-white'
                : `${theme.btnSecondary} hover:bg-orange-500/20`
            }`}
          >
            Monthly Reports
          </button>
          <button
            onClick={() => updateReportType('period')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              reportType === 'period'
                ? 'bg-cyan-500 text-white'
                : `${theme.btnSecondary} hover:bg-cyan-500/20`
            }`}
          >
            Period Reports
          </button>
        </div>

        {/* Run Control Card */}
        <div className={`backdrop-blur-sm rounded-2xl border p-6 mb-6 transition-colors duration-300 ${theme.card}`} style={{ overflow: 'visible' }}>
          <div className="flex flex-wrap items-end gap-6" style={{ overflow: 'visible' }}>
            {/* Date Range */}
            <div className="flex gap-4" style={{ overflow: 'visible' }}>
              <div style={{ overflow: 'visible' }}>
                <label className={`text-xs uppercase tracking-wider block mb-2 ${theme.textMuted}`}>
                  {reportType === 'monthly' ? 'Từ tháng' : 'Từ ngày'}
                </label>
                <div className="relative" style={{ overflow: 'visible' }}>
                  <Calendar className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${theme.inputIcon} pointer-events-none z-10`} />
                  <DatePicker
                    selected={startDate ? parse(startDate, 'yyyy-MM-dd', new Date()) : null}
                    onChange={(date) => {
                      if (date) {
                        // For monthly reports, always use start of month
                        const dateToUse = reportType === 'monthly' ? startOfMonth(date) : date;
                        setStartDate(format(dateToUse, 'yyyy-MM-dd'));
                      } else {
                        setStartDate('');
                      }
                    }}
                    disabled={isRunning}
                    isDark={isDark}
                    showMonthYearPicker={reportType === 'monthly'}
                    className={`pl-10 pr-4 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500/50 transition-colors w-44 ${theme.input}`}
                    placeholderText={reportType === 'monthly' ? 'Chọn tháng bắt đầu' : 'Chọn ngày bắt đầu'}
                  />
                </div>
              </div>
              <div style={{ overflow: 'visible' }}>
                <label className={`text-xs uppercase tracking-wider block mb-2 ${theme.textMuted}`}>
                  {reportType === 'monthly' ? 'Đến tháng' : 'Đến ngày'}
                </label>
                <div className="relative" style={{ overflow: 'visible' }}>
                  <Calendar className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${theme.inputIcon} pointer-events-none z-10`} />
                  <DatePicker
                    selected={endDate ? parse(endDate, 'yyyy-MM-dd', new Date()) : null}
                    onChange={(date) => {
                      if (date) {
                        // For monthly reports, always use end of month
                        const dateToUse = reportType === 'monthly' ? endOfMonth(date) : date;
                        setEndDate(format(dateToUse, 'yyyy-MM-dd'));
                      } else {
                        setEndDate('');
                      }
                    }}
                    disabled={isRunning}
                    isDark={isDark}
                    showMonthYearPicker={reportType === 'monthly'}
                    className={`pl-10 pr-4 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500/50 transition-colors w-44 ${theme.input}`}
                    placeholderText={reportType === 'monthly' ? 'Chọn tháng kết thúc' : 'Chọn ngày kết thúc'}
                  />
                </div>
              </div>
            </div>

            {/* Run Button */}
            <button
              onClick={startRun}
              disabled={isRunning}
              className={`px-8 py-2.5 rounded-lg font-medium text-sm flex items-center gap-2.5 transition-all ${isRunning
                ? `${theme.btnSecondary} cursor-not-allowed opacity-50`
                : 'bg-gradient-to-r from-cyan-500 to-blue-500 text-white hover:shadow-lg hover:shadow-cyan-500/25 hover:-translate-y-0.5'
                }`}
            >
              {isRunning ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Đang chạy...
                </>
              ) : (
                <>
                  <Play className="w-4 h-4" />
                  Chạy {reportType === 'monthly' ? 'Monthly' : reportType === 'period' ? 'Period' : 'Weekly'} Report
                </>
              )}
            </button>
          </div>

          {/* Quick Date Selection */}
          {reportType === 'weekly' ? (
            <div className="flex flex-wrap items-center gap-2 mt-4">
              <span className={`text-xs ${theme.textMuted} mr-2`}>Chọn nhanh:</span>
              {[1, 2, 3, 4, 5, 6].map((weeks) => (
                <button
                  key={weeks}
                  onClick={() => setWeeksRange(weeks)}
                  disabled={isRunning}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${selectedWeeks === weeks && !customWeeks
                    ? 'bg-cyan-500 text-white'
                    : `${theme.btnSecondary} hover:bg-cyan-500/20`
                    } ${isRunning ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  {weeks} tuần
                </button>
              ))}
              <div className="flex items-center gap-1.5 ml-2">
                <input
                  type="number"
                  placeholder="N"
                  min="1"
                  max="52"
                  value={customWeeks}
                  onChange={(e) => handleCustomWeeks(e.target.value)}
                  disabled={isRunning}
                  className={`w-14 px-2 py-1.5 border rounded-lg text-xs text-center focus:outline-none focus:ring-2 focus:ring-cyan-500/50 ${theme.input} ${isRunning ? 'opacity-50 cursor-not-allowed' : ''}`}
                />
                <span className={`text-xs ${theme.textMuted}`}>tuần</span>
              </div>
            </div>
          ) : reportType === 'monthly' ? (
            <div className="flex flex-wrap items-center gap-2 mt-4">
              <span className={`text-xs ${theme.textMuted} mr-2`}>Chọn nhanh:</span>
              {MONTHLY_PRESETS.map((preset) => (
                <button
                  key={preset.id}
                  onClick={() => handleMonthlyPreset(preset.id)}
                  disabled={isRunning}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                    selectedMonthlyPreset === preset.id
                      ? 'bg-orange-500 text-white'
                      : `${theme.btnSecondary} hover:bg-orange-500/20`
                  } ${isRunning ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  {preset.label}
                </button>
              ))}
            </div>
          ) : (
            <div className="flex flex-wrap items-center gap-2 mt-4">
              <span className={`text-xs ${theme.textMuted} mr-2`}>Chọn nhanh:</span>
              {PERIOD_PRESETS.map((preset) => (
                <button
                  key={preset.id}
                  onClick={() => handlePeriodPreset(preset.id)}
                  disabled={isRunning}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                    selectedPeriodPreset === preset.id
                      ? 'bg-cyan-500 text-white'
                      : `${theme.btnSecondary} hover:bg-cyan-500/20`
                  } ${isRunning ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  {preset.label}
                </button>
              ))}
            </div>
          )}

          {/* Status indicator row */}
          <div className="flex items-center mt-4">
            {/* Status indicator */}
            <div className="flex-1 flex justify-end">
              {runStatus && (
                <div
                  className={`flex items-center gap-3 px-4 py-2.5 rounded-lg ${runStatus.status === 'running'
                    ? `${theme.infoBg} ${theme.infoText}`
                    : runStatus.status === 'completed'
                      ? `${theme.successBg} ${theme.successText}`
                      : `${theme.errorBg} ${theme.errorText}`
                    }`}
                >
                  {runStatus.status === 'running' ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : runStatus.status === 'completed' ? (
                    <CheckCircle className="w-4 h-4" />
                  ) : (
                    <XCircle className="w-4 h-4" />
                  )}
                  <span className="text-sm font-medium">
                    {runStatus.status === 'running'
                      ? `${runStatus.progress}% - ${runStatus.currentStep}`
                      : runStatus.status === 'completed'
                        ? 'Hoàn thành!'
                        : runStatus.error || 'Thất bại'}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Progress bar */}
          {runStatus?.status === 'running' && (
            <div className="mt-4">
              <div className={`w-full rounded-full h-1.5 overflow-hidden ${theme.progressBg}`}>
                <div
                  className="bg-gradient-to-r from-cyan-500 to-blue-500 h-full rounded-full transition-all duration-500"
                  style={{ width: `${runStatus.progress}%` }}
                />
              </div>
            </div>
          )}

          {/* Log Section */}
          {logEntries.length > 0 && (
            <div className="mt-4 pt-4 border-t border-white/[0.06]">
              <div className="flex items-center gap-2 mb-3">
                <Terminal className={`w-4 h-4 ${theme.icon}`} />
                <h3 className={`text-sm font-medium ${theme.text}`}>Tiến trình</h3>
              </div>
              <div className={`rounded-lg ${isDark ? 'bg-black/20' : 'bg-gray-50'} p-3 max-h-64 overflow-y-auto`}>
                <div className="space-y-2 font-mono text-xs">
                  {logEntries.map((entry) => (
                    <div key={entry.id} className="flex items-start gap-3">
                      <div className="flex-shrink-0 mt-0.5">
                        {entry.type === 'running' && (
                          <Loader2 className={`w-3.5 h-3.5 ${theme.infoText} animate-spin`} />
                        )}
                        {entry.type === 'success' && (
                          <CheckCircle className={`w-3.5 h-3.5 ${theme.successText}`} />
                        )}
                        {entry.type === 'error' && (
                          <XCircle className={`w-3.5 h-3.5 ${theme.errorText}`} />
                        )}
                        {entry.type === 'info' && (
                          <div className={`w-3.5 h-3.5 rounded-full ${theme.infoText} bg-current`} />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <span className={`${theme.textMuted} mr-2`}>
                          {format(entry.timestamp, 'HH:mm:ss')}
                        </span>
                        <span className={entry.type === 'error' ? theme.errorText : entry.type === 'success' ? theme.successText : theme.textSecondary}>
                          {entry.message}
                        </span>
                      </div>
                    </div>
                  ))}
                  <div ref={logEndRef} />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Versions List */}
        <div className={`backdrop-blur-sm rounded-2xl border transition-colors duration-300 mt-8 ${theme.card}`} style={{ overflowX: 'hidden', overflowY: 'visible' }}>
          <div className={`flex items-center justify-between px-6 py-4 border-b ${theme.borderLight}`}>
            <div className="flex items-center gap-3">
              <Clock className={`w-5 h-5 ${theme.icon}`} />
              <h2 className={`font-medium ${theme.text}`}>Lịch sử chạy</h2>
              <span className={`text-sm ${theme.textMuted}`}>
                ({versions.filter(v => (v.reportType || 'weekly') === reportType).length})
              </span>
            </div>
            <button
              onClick={loadVersions}
              className={`p-2 transition-colors rounded-lg hover:bg-black/5 ${theme.icon} ${theme.iconHover}`}
              title="Refresh"
            >
              <RefreshCw className={`w-4 h-4 ${isLoadingVersions ? 'animate-spin' : ''}`} />
            </button>
          </div>

          {isLoadingVersions ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="w-6 h-6 text-cyan-500 animate-spin" />
            </div>
          ) : versions.filter(v => (v.reportType || 'weekly') === reportType).length === 0 ? (
            <div className="text-center py-16">
              <FileSpreadsheet className={`w-10 h-10 mx-auto mb-3 ${theme.icon}`} />
              <p className={`text-sm ${theme.textMuted}`}>
                Chưa có {reportType === 'monthly' ? 'monthly' : reportType === 'period' ? 'period' : 'weekly'} report nào
              </p>
            </div>
          ) : (
            <div className={`divide-y ${theme.divider}`}>
              {versions.filter(v => (v.reportType || 'weekly') === reportType).map((version) => (
                <div key={version.id} className="group">
                  {/* Version Header */}
                  <div
                    className={`flex items-center gap-4 px-6 py-4 cursor-pointer transition-colors ${theme.cardHover}`}
                    onClick={() => {
                      // Open visualization in new tab if completed && has summary file
                      if (version.status === 'completed' && version.files.includes('summary.csv')) {
                        window.open(`/visualization/${version.id}`, '_blank');
                      }
                    }}
                  >
                    <div className={`w-2 h-2 rounded-full ${version.status === 'completed' ? theme.successDot : theme.errorDot}`} />

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3">
                        <span className={`font-mono text-sm ${theme.text}`}>{version.id}</span>

                        {/* Report Type Badge */}
                        <span className={`text-xs px-2 py-0.5 rounded-full ${
                          version.reportType === 'monthly'
                            ? 'bg-orange-500/10 text-orange-400'
                            : version.reportType === 'period'
                              ? 'bg-purple-500/10 text-purple-400'
                              : 'bg-cyan-500/10 text-cyan-400'
                        }`}>
                          {version.reportType === 'monthly' ? 'Monthly' : version.reportType === 'period' ? 'Period' : 'Weekly'}
                        </span>

                        {version.dateRange.startDate && (
                          <span className={`text-xs ${theme.textMuted}`}>
                            {formatDateRange(version.dateRange.startDate, version.dateRange.endDate)}
                          </span>
                        )}
                      </div>
                      <p className={`text-xs mt-0.5 ${theme.textMuted}`}>
                        Ngày chạy: {formatTimestamp(version.timestamp)}
                        {version.reportType === 'weekly' && version.weeksProcessed > 0 && ` • ${version.weeksProcessed} tuần`}
                        {version.reportType === 'monthly' && version.monthsProcessed > 0 && ` • ${version.monthsProcessed} tháng`}
                        {version.files.length > 0 && ` • ${version.files.length} files`}
                      </p>
                    </div>

                    <div className="flex items-center gap-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteVersion(version.id);
                        }}
                        className={`p-2 transition-colors rounded-lg hover:bg-red-500/10 ${theme.icon} hover:text-red-500`}
                        title="Xóa"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                      {version.status === 'completed' && version.files.includes('summary.csv') && (
                        <a
                          href={`/visualization/${version.id}`}
                          onClick={(e) => e.stopPropagation()}
                          className={`px-3 py-1.5 transition-colors rounded-lg bg-emerald-500/10 hover:bg-emerald-500/20 ${theme.successText} flex items-center gap-1.5 text-xs font-medium`}
                          title="Visualization"
                        >
                          <BarChart className="w-3.5 h-3.5" />
                          Visualization
                        </a>
                      )}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setExpandedVersion(expandedVersion === version.id ? null : version.id);
                        }}
                        className={`p-2 transition-colors rounded-lg hover:bg-white/5 ${theme.icon} ${theme.iconHover}`}
                        title={expandedVersion === version.id ? "Thu gọn files" : "Xem files"}
                      >
                        {expandedVersion === version.id ? (
                          <ChevronUp className="w-4 h-4" />
                        ) : (
                          <ChevronDown className="w-4 h-4" />
                        )}
                      </button>
                    </div>
                  </div>

                  {/* Expanded Files */}
                  {expandedVersion === version.id && version.files.length > 0 && (
                    <div className="px-6 pb-4">
                      <div className={`rounded-xl border ${theme.cardInner} divide-y ${theme.divider}`}>
                        {version.files.map((file) => (
                          <div
                            key={file}
                            className={`flex items-center justify-between px-4 py-3 transition-colors ${theme.cardHover}`}
                          >
                            <div className="flex items-center gap-3">
                              <FileSpreadsheet className={`w-4 h-4 ${theme.icon}`} />
                              <span className={`text-sm font-mono ${theme.textSecondary}`}>{file}</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openPreview(version.id, file);
                                }}
                                className={`p-2 transition-colors rounded-lg hover:bg-cyan-500/10 ${theme.icon} hover:text-cyan-500`}
                                title="Xem trước"
                              >
                                <Eye className="w-4 h-4" />
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  downloadFile(version.id, file);
                                }}
                                className={`p-2 transition-colors rounded-lg hover:bg-cyan-500/10 ${theme.icon} hover:text-cyan-500`}
                                title="Tải xuống"
                              >
                                <Download className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </>}
      </div>

      {/* Preview Sidebar */}
      {previewFile && (
        <>
          {/* Backdrop */}
          <div
            className={`fixed inset-0 bg-black/20 backdrop-blur-sm z-40 transition-opacity duration-300`}
            onClick={() => {
              setPreviewFile(null);
              setPreviewData(null);
              setPreviewError(null);
            }}
          />

          {/* Sidebar */}
          <div className={`fixed top-0 right-0 bottom-0 w-full max-w-2xl ${theme.modalBg} ${theme.borderLight} border-l shadow-2xl z-50 flex flex-col transition-transform duration-300 ease-in-out`}>
            {/* Header */}
            <div className={`flex items-center justify-between px-6 py-4 border-b ${theme.borderLight} flex-shrink-0`}>
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <FileSpreadsheet className="w-5 h-5 text-cyan-500 flex-shrink-0" />
                <div className="min-w-0 flex-1">
                  <span className={`font-medium ${theme.text} block truncate`}>{previewFile.fileName}</span>
                  {previewData && (
                    <span className={`text-xs ${theme.textMuted} block`}>
                      {previewData.previewRows} / {previewData.totalRows} dòng
                    </span>
                  )}
                </div>
              </div>
              <button
                onClick={() => {
                  setPreviewFile(null);
                  setPreviewData(null);
                  setPreviewError(null);
                }}
                className={`p-2 transition-colors rounded-lg hover:bg-red-500/10 ${theme.icon} hover:text-red-500 flex-shrink-0 ml-2`}
                title="Đóng"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-auto">
              {previewLoading ? (
                <div className="flex items-center justify-center py-16">
                  <Loader2 className="w-6 h-6 text-cyan-500 animate-spin" />
                </div>
              ) : previewError ? (
                <div className={`text-center py-16 ${theme.errorText}`}>
                  <XCircle className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p className="font-medium">{previewError}</p>
                </div>
              ) : previewData ? (
                <div className="overflow-x-auto h-full">
                  <table className="w-full text-sm">
                    <thead className="sticky top-0 z-10">
                      <tr className={theme.tableHeaderBg}>
                        {previewData.headers.map((header, i) => (
                          <th
                            key={i}
                            className={`px-4 py-3 text-left font-medium text-xs uppercase tracking-wider whitespace-nowrap border-b ${theme.textMuted} ${theme.borderLight}`}
                          >
                            {header}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className={`divide-y ${theme.divider}`}>
                      {previewData.rows.map((row, i) => (
                        <tr key={i} className={theme.tableRowHover}>
                          {row.map((cell, j) => (
                            <td
                              key={j}
                              className={`px-4 py-3 ${theme.textSecondary}`}
                            >
                              <div className="max-w-xs truncate" title={String(cell)}>
                                {cell}
                              </div>
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : null}
            </div>

            {/* Footer */}
            <div className={`px-6 py-4 border-t flex justify-end gap-3 ${theme.borderLight} flex-shrink-0`}>
              <button
                onClick={() => downloadFile(previewFile.versionId, previewFile.fileName)}
                className="px-5 py-2 bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded-lg text-sm font-medium hover:shadow-lg hover:shadow-cyan-500/25 transition-all flex items-center gap-2"
              >
                <Download className="w-4 h-4" />
                Tải xuống
              </button>
              <button
                onClick={() => {
                  setPreviewFile(null);
                  setPreviewData(null);
                  setPreviewError(null);
                }}
                className={`px-5 py-2 rounded-lg text-sm font-medium transition-colors ${theme.btnSecondary}`}
              >
                Đóng
              </button>
            </div>
          </div>
        </>
      )}

      {/* Confirm Modal */}
      {confirmModal && (
        <ConfirmModal
          isOpen={confirmModal.isOpen}
          message={confirmModal.message}
          confirmText="Xóa"
          cancelText="Hủy"
          confirmVariant="danger"
          onConfirm={confirmModal.onConfirm}
          onCancel={() => setConfirmModal(null)}
          isDark={isDark}
        />
      )}

      {/* Alert Modal */}
      {alertModal && (
        <AlertModal
          isOpen={alertModal.isOpen}
          type={alertModal.type}
          message={alertModal.message}
          onClose={() => setAlertModal(null)}
          isDark={isDark}
        />
      )}
    </div>
  );
}
